/**
 * Application chrome.
 *
 * Mirrors the approved prototype shell (`UIs Updated 11July` → `assets/js/app-shell.js`):
 * a single top header carrying the brand, the project selector, the primary nav,
 * the system launcher and the profile menu. The prototype's stylesheet ships in
 * this app verbatim as `src/styles/edifice.css`, so the class names below are the
 * prototype's own — keep them, and prefer adding a rule there over inlining styles.
 *
 * Nav content switches between the user and admin sets exactly as the prototype
 * does (profile menu → "Admin Panel" / "Exit to User View"), with each entry
 * additionally gated on the signed-in user's permissions.
 */

import {
  BarChart2,
  Bell,
  Building2,
  Camera,
  ChevronDown,
  Database,
  FolderKanban,
  Grid3x3,
  HelpCircle,
  Layers,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeft,
  PanelLeftClose,
  Settings,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ProjectProvider, useSelectedProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/features/auth/useAuth';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useProjects } from '@/features/projects/hooks';
import type { Project } from '@/features/projects/types';
import { roleLabel } from '@/features/users/constants';

/* ── Navigation ──────────────────────────────────────────────────────────── */

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
  /** Optional extra gate on top of the role's own nav list. */
  permission?: string;
}

/**
 * Two systems share one shell. The sidebar's top group carries the entries of
 * the ACTIVE system only (Site Agent or Drawing Validation — switched from the
 * launcher at the sidebar's bottom); the "General" group below it is shared
 * across both systems.
 *
 * "Site Queries" is deliberately absent: a site query and an observation are the
 * same record, surfaced on the Observations page (tagged red/blue by kind).
 */
type SystemKey = 'site-agent' | 'validation';

/** Site Agent nav — full set for Admin/PM, focused for Site Agent User.
 *  Projects is deliberately NOT here: projects are reached from the dashboard
 *  (and the header's project selector), where selecting one makes every other
 *  menu operate on it. Drawing Validation Users only see Site Agent as
 *  read-only viewers (no capture). */
const SITE_AGENT_NAV: Record<string, NavItem[]> = {
  SUPER_ADMIN: [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/observations', label: 'Observations', icon: Database },
    { to: '/reports', label: 'Reports', icon: BarChart2 },
  ],
  MANAGER: [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/observations', label: 'Observations', icon: Database },
    { to: '/reports', label: 'Reports', icon: BarChart2 },
  ],
  OFFICE_TEAM: [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/observations', label: 'Observations', icon: Database },
  ],
  SITE_TEAM: [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/capture', label: 'Capture', icon: Camera },
    { to: '/observations', label: 'Observations', icon: Database },
    { to: '/reports', label: 'Reports', icon: BarChart2 },
  ],
};

/** Validation nav — placeholder for now (under development). */
const VALIDATION_NAV: Record<string, NavItem[]> = {
  SUPER_ADMIN: [{ to: '/validations', label: 'Validation Dashboard', icon: Layers }],
  MANAGER: [{ to: '/validations', label: 'Validation Dashboard', icon: Layers }],
  OFFICE_TEAM: [{ to: '/validations', label: 'Validation Dashboard', icon: Layers }],
  SITE_TEAM: [{ to: '/validations', label: 'Validation Dashboard', icon: Layers }],
};

/** General group — shared across both systems. Admin/PM get team management. */
const GENERAL_NAV: Record<string, NavItem[]> = {
  SUPER_ADMIN: [
    { to: '/admin/users', label: 'Team', icon: Users },
    { to: '/notifications', label: 'Notifications', icon: Bell },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
    { to: '/help', label: 'Help & Guides', icon: HelpCircle },
  ],
  MANAGER: [
    { to: '/admin/users', label: 'Team', icon: Users },
    { to: '/notifications', label: 'Notifications', icon: Bell },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
    { to: '/help', label: 'Help & Guides', icon: HelpCircle },
  ],
  OFFICE_TEAM: [
    { to: '/notifications', label: 'Notifications', icon: Bell },
    { to: '/help', label: 'Help & Guides', icon: HelpCircle },
  ],
  SITE_TEAM: [
    { to: '/notifications', label: 'Notifications', icon: Bell },
    { to: '/help', label: 'Help & Guides', icon: HelpCircle },
  ],
};

/** Systems in the launcher. Access is gated by permission strings:
 *  - Site Agent: `sa:access` (or SUPER_ADMIN/MANAGER — always granted)
 *  - Validation: `val:access` (or SUPER_ADMIN/MANAGER — always granted)
 *  Admins can grant/revoke these per-user in the Team management page.
 */
const SYSTEMS = [
  {
    key: 'site-agent',
    name: 'Site Agent',
    desc: 'Site documentation & intelligence',
    color: '#f59e0b',
    icon: Camera,
    permission: 'sa:access',
    to: '/',
  },
  {
    key: 'validation',
    name: 'Drawing Validation',
    desc: 'AI-powered validation (under development)',
    color: '#6366f1',
    icon: Layers,
    permission: 'val:access',
    to: '/validations',
  },
];

function initials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const STATUS_DOT: Record<string, string> = {
  active: 'var(--color-green-500)',
  on_hold: 'var(--color-amber-400)',
  completed: 'var(--color-blue-400)',
  archived: 'var(--color-gray-300)',
};

/** Routes whose URL carries the project id, so switching project must rewrite it. */
const PROJECT_URL_PATTERNS = [/^\/observations\/[^/]+/, /^\/site-queries\/[^/]+/];

/* ── Project selector ────────────────────────────────────────────────────── */

function ProjectSelector() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { selectedProject, setSelectedProject } = useSelectedProject();
  const { data, isLoading } = useProjects({ limit: 100, offset: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  const projects = data?.data ?? [];

  // No auto-selection: working without a project is a supported state — the
  // project-specific pages prompt for one. A phantom selection would hide that.

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function select(project: Project) {
    setSelectedProject(project);
    setOpen(false);
    setSearch('');
    for (const pattern of PROJECT_URL_PATTERNS) {
      if (pattern.test(location.pathname)) {
        const segment = location.pathname.split('/')[1];
        navigate(`/${segment}/${project.id}`, { replace: true });
        return;
      }
    }
  }

  const q = search.trim().toLowerCase();
  const shown = q
    ? projects.filter((p) => `${p.name} ${p.code}`.toLowerCase().includes(q))
    : projects;

  return (
    <div className="project-selector" ref={ref}>
      <button className="project-selector-btn" onClick={() => setOpen((v) => !v)}>
        <Building2 size={12} />
        <span>{selectedProject?.name ?? (isLoading ? 'Loading…' : 'Select project')}</span>
        <ChevronDown size={10} />
      </button>

      {open && (
        <div className="project-dropdown open">
          <input
            className="project-dropdown-search"
            placeholder="Search projects…"
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
          />
          <div>
            {shown.length === 0 ? (
              <div className="project-dropdown-item" style={{ color: 'var(--color-gray-400)' }}>
                {isLoading ? 'Loading…' : 'No projects found'}
              </div>
            ) : (
              shown.map((p) => (
                <div
                  key={p.id}
                  className={`project-dropdown-item ${p.id === selectedProject?.id ? 'active' : ''}`}
                  onClick={() => select(p)}
                >
                  <span
                    className="project-dot"
                    style={{ background: STATUS_DOT[p.status] ?? 'var(--color-gray-300)' }}
                  />
                  <span>{p.name}</span>
                </div>
              ))
            )}
          </div>
          <div className="project-dropdown-footer">
            {selectedProject && (
              <div
                className="project-dropdown-item"
                style={{ color: 'var(--color-gray-400)', fontSize: '0.75rem' }}
                onClick={() => {
                  setSelectedProject(null);
                  setOpen(false);
                }}
              >
                <X size={12} />
                <span>Clear selection</span>
              </div>
            )}
            <Link
              to="/projects"
              className="project-dropdown-item"
              style={{ color: 'var(--color-gray-400)', fontSize: '0.75rem' }}
              onClick={() => setOpen(false)}
            >
              <FolderKanban size={12} />
              <span>Go to Projects</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── System launcher ─────────────────────────────────────────────────────── */

function SystemLauncher({
  system,
  onSwitch,
  available,
}: {
  system: SystemKey;
  onSwitch: (key: SystemKey) => void;
  available: typeof SYSTEMS;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = SYSTEMS.find((s) => s.key === system) ?? SYSTEMS[0];

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="system-launcher" ref={ref}>
      <button className="system-launcher-btn" onClick={() => setOpen((v) => !v)}>
        <Grid3x3 size={14} />
        <span>{current?.name ?? 'Site Agent'}</span>
        <ChevronDown size={10} />
      </button>

      {open && (
        <div className="system-launcher-dropdown open">
          <div className="launcher-title">Switch System</div>
          {SYSTEMS.map((s) => {
            const isAvailable = available.some((a) => a.key === s.key);
            return (
            <div
              key={s.key}
              className={`system-launcher-item ${s.key === current?.key ? 'active' : ''}`}
              style={isAvailable ? undefined : { opacity: 0.4, cursor: 'default' }}
              title={isAvailable ? undefined : 'No access — contact your admin'}
              onClick={() => {
                if (!isAvailable) return;
                setOpen(false);
                onSwitch(s.key as SystemKey);
              }}
            >
              <div className="launcher-icon" style={{ background: `${s.color}15`, color: s.color }}>
                <s.icon size={20} />
              </div>
              <div className="launcher-info">
                <h4>{s.name}</h4>
                <p>{isAvailable ? s.desc : 'No access'}</p>
              </div>
              {s.key === current?.key && (
                <span className="badge badge-dark" style={{ marginLeft: 'auto', fontSize: '0.5rem' }}>
                  Active
                </span>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Profile menu ────────────────────────────────────────────────────────── */

function ProfileMenu() {
  const [open, setOpen] = useState(false);
  const { me, signOut } = useAuth();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!me) return null;
  const name = me.user.full_name || me.user.email;

  return (
    <div className="user-profile" ref={ref}>
      <button className="user-profile-btn" onClick={() => setOpen((v) => !v)}>
        <div className="user-avatar" style={{ background: 'var(--color-gray-900)' }}>
          {initials(name)}
        </div>
        <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
          <div className="user-name">{name}</div>
          <div className="user-role-tag">{roleLabel(me.user.role)}</div>
        </div>
        <ChevronDown size={10} />
      </button>

      {open && (
        <div className="user-profile-dropdown open">
          <div className="profile-header">
            <div className="avatar-lg" style={{ background: 'var(--color-gray-900)' }}>
              {initials(name)}
            </div>
            <div>
              <h4>{name}</h4>
              <p>{me.user.email}</p>
            </div>
          </div>

          <Link to="/notifications" className="profile-menu-item" onClick={() => setOpen(false)}>
            <UserIcon size={15} />
            Notifications
          </Link>

          <div className="profile-menu-divider" />
          <button
            className="profile-menu-item danger"
            style={{ width: '100%' }}
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            <LogOut size={15} />
            Log Out
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Shell ───────────────────────────────────────────────────────────────── */

const SIDEBAR_COLLAPSED_KEY = 'edifice:sidebarCollapsed';
const ACTIVE_SYSTEM_KEY = 'edifice:activeSystem';

export function AppShell() {
  const { me, hasPermission } = useAuth();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [system, setSystem] = useState<SystemKey>(() =>
    localStorage.getItem(ACTIVE_SYSTEM_KEY) === 'validation' ? 'validation' : 'site-agent',
  );
  const location = useLocation();
  const navigate = useNavigate();

  // Any navigation closes the mobile drawer; deep links into a system's pages
  // also make that system active (e.g. a shared /validations URL).
  useEffect(() => {
    setMobileOpen(false);
    const inValidation = location.pathname.startsWith('/validations');
    const inSiteAgent = /^\/($|capture|observations|reports)/.test(location.pathname);
    if (inValidation) selectSystem('validation', false);
    else if (inSiteAgent) selectSystem('site-agent', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function selectSystem(key: SystemKey, jump = true) {
    setSystem(key);
    localStorage.setItem(ACTIVE_SYSTEM_KEY, key);
    if (jump) navigate(key === 'validation' ? '/validations' : '/');
  }

  function toggleCollapsed() {
    setCollapsed((v) => {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, v ? '0' : '1');
      return !v;
    });
  }

  const role = me?.user.role ?? '';
  const isAdmin = role === 'SUPER_ADMIN' || role === 'MANAGER';
  const byPermission = (item: NavItem) => !item.permission || hasPermission(item.permission);

  // System access: admins always have both; others need the permission string.
  const canAccessSystem = (s: typeof SYSTEMS[number]) =>
    isAdmin || hasPermission(s.permission);
  const availableSystems = SYSTEMS.filter(canAccessSystem);

  const systemNav = system === 'validation' ? VALIDATION_NAV : SITE_AGENT_NAV;
  const systemItems = (systemNav[role] ?? systemNav['OFFICE_TEAM'] ?? []).filter(byPermission);
  const generalItems = (GENERAL_NAV[role] ?? GENERAL_NAV['OFFICE_TEAM'] ?? []).filter(
    byPermission,
  );
  // The selected project drives every project-specific page, so the selector
  // lives in the header for EVERY role (admins included), Site Agent only.
  const showProjectSelector = system === 'site-agent';

  return (
    <ProjectProvider>
      <div className="app-shell with-sidebar">
        <aside
          className={`app-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
        >
          <div className="app-sidebar-header">
            <Link to="/" className="brand">
              <div className="brand-icon">
                <img src="/favicon.svg" alt="Edifice logo" className="brand-logo" />
              </div>
              <div className="brand-text">
                <div className="brand-title">Edifice AI Automation</div>
                <div className="brand-sub">by Architerrax</div>
              </div>
            </Link>
          </div>

          <nav className="app-sidebar-nav">
            <div className="app-sidebar-group-title">
              {system === 'validation' ? 'Drawing Validation' : 'Site Agent'}
            </div>
            {systemItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={15} />
                <span>{item.label}</span>
              </NavLink>
            ))}

            {generalItems.length > 0 && (
              <>
                <div className="app-sidebar-group-title" style={{ marginTop: 14 }}>
                  General
                </div>
                {generalItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end ?? false}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon size={15} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          <div className="app-sidebar-footer">
            <SystemLauncher
              system={system}
              onSwitch={selectSystem}
              available={availableSystems}
            />
          </div>
        </aside>

        {mobileOpen && (
          <div className="app-sidebar-backdrop" onClick={() => setMobileOpen(false)} />
        )}

        <div className="app-main-col">
          <header className="app-header">
            <div className="header-inner wide">
              <div className="flex items-center gap-2">
                <button
                  className="app-sidebar-toggle app-sidebar-mobile-toggle"
                  onClick={() => setMobileOpen((v) => !v)}
                  aria-label="Open navigation"
                >
                  {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
                <button
                  className="app-sidebar-toggle app-sidebar-desktop-toggle"
                  onClick={toggleCollapsed}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
                </button>
                {/* Selected project — shown on top of the header; the same
                    control switches or clears it. */}
                {showProjectSelector && <ProjectSelector />}
              </div>

              <div className="flex items-center gap-2">
                <NotificationBell />
                <ProfileMenu />
              </div>
            </div>
          </header>

          <main className="main-content wide animate-in">
            <Outlet />
          </main>
        </div>
      </div>
    </ProjectProvider>
  );
}
